import { useTranslation } from "react-i18next";
import { MarketingNav } from "@/components/landing/MarketingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { Target, Eye, Lightbulb, Linkedin } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { SchemaMarkup, organizationSchema, generateBreadcrumbSchema } from "@/components/SchemaMarkup";
import { Breadcrumbs } from "@/components/Breadcrumbs";

import filipiPhoto from "@/assets/founders/filipi-simas.png";
import julianoPhoto from "@/assets/founders/juliano-belle.png";

const founders = [
  {
    name: "Filipi Simas",
    role: "Co-Founder",
    photo: filipiPhoto,
    linkedin: "https://www.linkedin.com/in/filipisimas/",
    bioPt:
      "Mais de 10 anos construindo e escalando máquinas de receita em SaaS. Filipi Simas é especialista em Revenue Operations, Customer Success e crescimento previsível — com passagem por empresas como Conta Azul e RD Station.\n\nReconhecido como Top 100 Customer Success Strategist, atua na interseção entre dados, processos e estratégia para destravar crescimento. Já estruturou e otimizou operações que conectam Marketing, Vendas e CS, transformando complexidade em sistemas simples, escaláveis e orientados a resultado.\n\nConstrói motores de receita que crescem com previsibilidade — não por sorte.",
    bioEn:
      "10+ years building and scaling revenue engines in SaaS. Filipi Simas is a Revenue Operations and Customer Success leader focused on predictable growth and operational excellence.\n\nPreviously at Conta Azul (leading SMB financial management SaaS platform in Latin America) and RD Station (Brazil's leading marketing automation and CRM platform), he led initiatives connecting Marketing, Sales, and Customer Success into unified, scalable revenue systems.\n\nRecognized as a Top 100 Customer Success Strategist, Filipi operates at the intersection of data, processes, and strategy — turning complexity into simple, high-performing growth engines.\n\nHe builds revenue machines that scale with predictability — not guesswork.",
  },
  {
    name: "Juliano Bellé",
    role: "Co-Founder",
    photo: julianoPhoto,
    linkedin: "https://www.linkedin.com/in/julianobelle/",
    bioPt:
      "Mais de uma década construindo a infraestrutura por trás de operações de receita em SaaS B2B. Juliano Bellé é especialista em Revenue Operations, Forecasting e arquitetura de CRM — com experiência em empresas como RD Station e Ambev.\n\nCombina visão estratégica com profundidade técnica para desenhar e implementar sistemas de RevOps que escalam. Da modelagem de forecast à implementação em CRM, transforma operações fragmentadas em máquinas de crescimento eficientes, previsíveis e orientadas a dados.\n\nFocado em uma coisa: fazer a receita acontecer com consistência.",
    bioEn:
      "10+ years designing and operating revenue infrastructure for B2B SaaS companies. Juliano Bellé is a Revenue Operations leader specialized in Forecasting, GTM strategy, and CRM architecture.\n\nPreviously at RD Station (Brazil's leading marketing automation and CRM platform) and Ambev (one of the largest beverage companies in the world and part of AB InBev), he developed a strong ability to connect strategic vision with technical execution.\n\nFrom forecasting models to CRM implementation, Juliano builds RevOps systems that turn fragmented operations into scalable, data-driven growth engines.\n\nFocused on one thing: making revenue predictable and repeatable.",
  },
];

export default function AboutPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith("pt");

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "Sobre a RevOps AI — Nossa Missão e Fundadores" : "About RevOps AI — Our Mission and Founders"}
        description={isPt ? "Conheça a equipe por trás do RevOps AI. Fundadores com 20+ anos de experiência combinada em RevOps, com passagens por RD Station, Conta Azul e Ambev." : "Meet the team behind RevOps AI. Founders with 20+ years of combined RevOps experience from RD Station, Conta Azul, and Ambev."}
        path="/about"
      />
      <SchemaMarkup schema={[
        organizationSchema,
        generateBreadcrumbSchema([
          { name: 'Home', url: 'https://revops.digitais.ai/' },
          { name: isPt ? 'Sobre' : 'About', url: 'https://revops.digitais.ai/about' },
        ]),
      ]} />
      <MarketingNav />
      <main>
        <section className="py-20 md:py-28 bg-[#F4F5FF]">
          <div className="container max-w-3xl mx-auto">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Sobre' : 'About' }]} />
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-[#1A1F3C] mb-6">
                {isPt ? "Sobre a RevOps AI" : "About RevOps AI"}
              </h1>
              <p className="text-lg text-[#1A1F3C]/60">
                {isPt
                  ? "Estamos construindo o futuro das operações de receita — onde cada decisão comercial é baseada em dados, não em intuição."
                  : "We're building the future of revenue operations — where every commercial decision is data-driven, not gut-driven."}
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container max-w-4xl mx-auto">
            {/* Mission / Vision / Values */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  icon: Target,
                  title: isPt ? "Missão" : "Mission",
                  desc: isPt
                    ? "Democratizar Revenue Operations, tornando inteligência de receita acessível para times de todos os tamanhos."
                    : "Democratize Revenue Operations, making revenue intelligence accessible to teams of all sizes.",
                },
                {
                  icon: Eye,
                  title: isPt ? "Visão" : "Vision",
                  desc: isPt
                    ? "Ser a plataforma de RevOps líder na América Latina, ajudando milhares de empresas a crescer com previsibilidade."
                    : "Be the leading RevOps platform in Latin America, helping thousands of companies grow with predictability.",
                },
                {
                  icon: Lightbulb,
                  title: isPt ? "Valores" : "Values",
                  desc: isPt
                    ? "Transparência nos dados, simplicidade na experiência, profundidade nas análises e impacto real nos resultados."
                    : "Data transparency, simplicity in experience, depth in analysis, and real impact on results.",
                },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="text-center">
                    <div className="inline-flex p-3 rounded-xl bg-[#4F6CFF]/10 mb-4">
                      <Icon className="h-6 w-6 text-[#4F6CFF]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#1A1F3C] mb-2">{item.title}</h3>
                    <p className="text-sm text-[#1A1F3C]/60">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Why RevOps AI Exists */}
            <div className="prose prose-lg max-w-none text-[#1A1F3C]/70 mb-12">
              <h2 className="text-2xl font-bold text-[#1A1F3C]">
                {isPt ? "Por que RevOps AI existe" : "Why RevOps AI exists"}
              </h2>
              <p>
                {isPt
                  ? "A maioria das empresas B2B opera receita no escuro. CRMs cheios de dados, mas sem respostas claras. Planilhas que ninguém confia. Forecasts baseados em feeling. E líderes que descobrem que perderam a meta só quando o mês já acabou."
                  : "Most B2B companies operate revenue in the dark. CRMs full of data, but no clear answers. Spreadsheets nobody trusts. Forecasts based on gut feeling. And leaders who only discover they missed target when the month is already over."}
              </p>
              <p>
                {isPt
                  ? "Isso acontece porque Revenue Operations — a disciplina que conecta Marketing, Vendas e CS em torno de uma operação de receita previsível — exige expertise rara. Profissionais seniores de RevOps são escassos, caros e levam meses para implementar processos do zero."
                  : "This happens because Revenue Operations — the discipline that connects Marketing, Sales, and CS around a predictable revenue engine — requires rare expertise. Senior RevOps professionals are scarce, expensive, and take months to implement processes from scratch."}
              </p>
              <p>
                {isPt
                  ? "RevOps AI nasceu para resolver esse gap. Combinamos 20+ anos de experiência em Revenue Operations com inteligência artificial para criar uma plataforma que pensa como um profissional sênior de RevOps — diagnosticando problemas, prevendo resultados e recomendando ações concretas. Tudo isso conectado nativamente ao seu CRM (HubSpot e RD Station), sem implementações complexas."
                  : "RevOps AI was built to solve this gap. We combined 20+ years of Revenue Operations experience with artificial intelligence to create a platform that thinks like a senior RevOps professional — diagnosing problems, predicting outcomes, and recommending concrete actions. All natively connected to your CRM (HubSpot and RD Station), with no complex implementations."}
              </p>
            </div>

            {/* Our Story */}
            <div className="prose prose-lg max-w-none text-[#1A1F3C]/70 mb-12">
              <h2 className="text-2xl font-bold text-[#1A1F3C]">{isPt ? "Nossa História" : "Our Story"}</h2>
              <p>
                {isPt
                  ? "Fundada em 2026 por Filipi Simas e Juliano Bellé, RevOps AI nasceu da frustração de líderes de vendas que operavam sem visibilidade. Times talentosos, CRMs repletos de dados, mas nenhuma resposta clara sobre por que a receita era imprevisível. Percebemos que o problema não era falta de dados — era falta de operação estruturada."
                  : "Founded in 2026 by Filipi Simas and Juliano Bellé, RevOps AI was born from the frustration of sales leaders operating without visibility. Talented teams, CRMs full of data, but no clear answers about why revenue was unpredictable. We realized the problem wasn't lack of data — it was lack of structured operations."}
              </p>
              <p>
                {isPt
                  ? "Com experiência combinada em empresas como RD Station, Conta Azul e Ambev, trouxemos para o produto tudo que aprendemos operando receita na prática: desde a modelagem de forecast e arquitetura de CRM até processos de handoff e métricas de performance."
                  : "With combined experience at companies like RD Station, Conta Azul, and Ambev, we brought into the product everything we learned operating revenue in practice: from forecast modeling and CRM architecture to handoff processes and performance metrics."}
              </p>
            </div>

            {/* What We Believe */}
            <div className="prose prose-lg max-w-none text-[#1A1F3C]/70 mb-12">
              <h2 className="text-2xl font-bold text-[#1A1F3C]">
                {isPt ? "No que acreditamos" : "What we believe"}
              </h2>
              <p>
                {isPt
                  ? "Acreditamos que receita previsível não é privilégio de empresas com times gigantes de BI e analistas. Qualquer empresa B2B que use um CRM já possui os dados necessários para operar com inteligência — falta apenas a camada de operação que conecta esses dados a decisões concretas."
                  : "We believe predictable revenue isn't a privilege reserved for companies with massive BI teams. Any B2B company using a CRM already has the data needed to operate with intelligence — what's missing is the operational layer that connects that data to concrete decisions."}
              </p>
              <p>
                {isPt
                  ? "Por isso construímos o RevOps AI como uma plataforma que pensa como um profissional sênior de Revenue Operations: diagnostica problemas de funil, prevê resultados com base matemática, identifica riscos antes que se tornem perdas e recomenda ações com responsável e prazo definidos. Não somos um dashboard genérico — somos o copiloto de receita que seu time precisa para crescer com consistência."
                  : "That's why we built RevOps AI as a platform that thinks like a senior Revenue Operations professional: it diagnoses funnel issues, forecasts results with mathematical models, identifies risks before they become losses, and recommends actions with clear ownership and timelines. We're not a generic dashboard — we're the revenue copilot your team needs to grow consistently."}
              </p>
              <p>
                {isPt
                  ? "Nossa tecnologia integra nativamente com HubSpot e RD Station, sincronizando deals, contatos, empresas e atividades automaticamente. Combinamos esses dados com inteligência artificial para gerar insights que antes exigiam semanas de análise manual — agora disponíveis em segundos, atualizados diariamente."
                  : "Our technology integrates natively with HubSpot and RD Station, automatically syncing deals, contacts, companies, and activities. We combine this data with artificial intelligence to generate insights that previously required weeks of manual analysis — now available in seconds, updated daily."}
              </p>
              <p>
                {isPt
                  ? "Nosso compromisso é com transparência total: cada métrica segue uma fórmula documentada, cada insight cita os dados de origem, e cada recomendação inclui o impacto estimado em receita. Acreditamos que a confiança nos dados é o alicerce de operações de receita de alto desempenho."
                  : "Our commitment is to full transparency: every metric follows a documented formula, every insight cites its source data, and every recommendation includes the estimated revenue impact. We believe that trust in data is the foundation of high-performing revenue operations."}
              </p>
            </div>

            {/* Company Facts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 p-6 rounded-xl bg-[#F4F5FF] border border-[#1A1F3C]/10">
              {[
                { label: isPt ? "Fundação" : "Founded", value: "2026" },
                { label: isPt ? "Localização" : "Location", value: isPt ? "Brasil" : "Brazil" },
                { label: isPt ? "Mercado" : "Market", value: "B2B SaaS" },
                { label: isPt ? "Experiência combinada" : "Combined experience", value: "20+" },
              ].map((fact, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl font-bold text-[#4F6CFF]">{fact.value}</p>
                  <p className="text-xs text-[#1A1F3C]/60 mt-1">{fact.label}</p>
                </div>
              ))}
            </div>

            {/* Founders */}
            <div>
              <h2 className="text-2xl font-bold text-[#1A1F3C] mb-8">{isPt ? "Fundadores" : "Founders"}</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {founders.map((founder) => (
                  <div key={founder.name} className="rounded-xl border border-[#1A1F3C]/10 p-6 bg-[#F4F5FF]/50">
                    <div className="flex items-center gap-4 mb-4">
                      <img
                        src={founder.photo}
                        alt={`${founder.name}, Co-Founder of RevOps AI`}
                        className="h-16 w-16 rounded-full object-cover border-2 border-[#4F6CFF]/20"
                        width={64}
                        height={64}
                      />
                      <div>
                        <h3 className="text-lg font-bold text-[#1A1F3C]">{founder.name}</h3>
                        <p className="text-sm text-[#4F6CFF] font-medium">{founder.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#1A1F3C]/70 leading-relaxed mb-4 whitespace-pre-line">
                      {isPt ? founder.bioPt : founder.bioEn}
                    </p>
                    <a
                      href={founder.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-[#4F6CFF] hover:text-[#3D5AE8] font-medium transition-colors"
                    >
                      <Linkedin className="h-4 w-4" />
                      LinkedIn
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
